import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Button,
} from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

type LogLevel = 'INFO' | 'ERROR' | 'WARN' | 'DEBUG'
type Props = { className?: string; children: ReactNode; conversationId?: string; userId?: string; level?: LogLevel }

const logLevels: { value: LogLevel | 'ALL'; content: string }[] = [
  { value: 'ALL', content: 'All' },
  { value: 'INFO', content: 'Info' },
  { value: 'ERROR', content: 'Error' },
  { value: 'WARN', content: 'Warn' },
  { value: 'DEBUG', content: 'Debug' },
]

export const FiltersPopover = ({ children, conversationId, level, userId, ...props }: Props) => {
  const [filters, setFilters] = useState<{
    conversationId?: string
    userId?: string
    level?: 'INFO' | 'ERROR' | 'WARN' | 'DEBUG'
  }>({ conversationId, level, userId })

  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent {...props} className="w-96">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Filters</h4>
            <p className="text-sm text-muted-foreground">Apply filters to you logs.</p>
          </div>
          <div className="grid gap-2">
            <div className="grid grid-cols-5 items-center gap-4">
              <Label className="col-span-2" htmlFor="loglevel">
                Log level
              </Label>
              <Select
                value={filters.level ?? logLevels[0]?.value}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, level: (value === logLevels[0]?.value ? undefined : value) as any }))
                }
              >
                <SelectTrigger
                  id="loglevel"
                  className="col-span-3"
                  // Prevent the click from closing the popover
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <SelectValue placeholder="Log level" />
                </SelectTrigger>
                <SelectContent
                  onCloseAutoFocus={(e) => {
                    // Prevent focus returning to popover which would close it
                    e.preventDefault()
                  }}
                >
                  {logLevels.map(({ content, value }) => (
                    <SelectItem key={value} value={value}>
                      {content}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-5 items-center gap-4">
              <Label className="col-span-2" htmlFor="conversationId">
                Conversation Id
              </Label>
              <Input
                id="conversationId"
                className="col-span-3 h-8"
                placeholder="Conversation Id"
                value={filters.conversationId}
                onChange={(event) => setFilters((prev) => ({ ...prev, conversationId: event.target.value }))}
              />
            </div>
            <div className="grid grid-cols-5 items-center gap-4">
              <Label className="col-span-2" htmlFor="userId">
                User Id
              </Label>
              <Input
                id="userId"
                value={filters.userId}
                onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
                placeholder="User Id"
                className="col-span-3 h-8"
              />
            </div>
          </div>
          <Button asChild className="ml-auto w-fit" size={'sm'} onClick={() => setOpen(false)}>
            <Link to="." search={(prev) => ({ ...prev, ...filters })}>
              Apply
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
